import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';

import { LayoutBaseProps } from '../layout.types';
import { Box, Flex } from '@withjoy/joykit';

import { FrameWrapper } from './components/FrameWrapper';
import { MainFrame } from './components/MainFrame';
import { AuxFrameProvider } from './components/AuxFrame';
import { Body } from './components/Body';
import { alohaConfig, useAlohaDesign } from './LayoutAloha.theme';
import { WelcomeCard } from './components/WelcomeCard';
import WelcomeVideo from '../../widgets/WelcomeVideo';
import { Footer } from '@apps/guest/packages/layout-engine/components/Footer';
import { alohaFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { PageContainer, CustomPageContainer, StyledFlexAloha } from './LayoutAloha.styles';
import { isInIframe } from '@shared/utils/isInIframe';
import { useLayoutAlohaController } from './LayoutAloha.controller';
import { useAlohaScroll } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/hooks/useAlohaScroll';
import { PagesRefs } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/LayoutAloha.types';
import { PreloadImages } from '@apps/guest/packages/layout-engine/layouts/LayoutAloha/components/PreloadImages/PreloadImages';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { InlineEditor, useWebsiteBannerOffset } from '@shared/components';
import { DesignLayoutType, EventPageType } from '@graphql/generated';
import { useColorPaletteProvider } from '../ColorPaletteProvider/ColorPaletteProvider';
import { WebsiteHeader } from './components/WebsiteHeader/WebsiteHeader';
import { pageWidgets, PreparedPages } from './components/Body/Body.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useFilterMigratedLegacyPages } from '../../hooks/useFilterMigratedLegacyPages';
import { getCustomArtMobileUrl, getCustomArtUrl, getEventMenuPropsFromEvent, getIsCustomArtEnabled } from '../layout.utils';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useTranslation } from '@shared/core';
import { CustomArt } from './components/CustomArt/CustomArt';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';

export interface LayoutAlohaProps extends Readonly<LayoutBaseProps> {
  setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
  setIsLastPageVisible?: (value: boolean) => void;
}

const { simple } = alohaConfig.applicatorHtmlProps;

const LayoutAloha: React.FC<LayoutAlohaProps> = ({ eventHandle, event, setLoadingTheme, setIsLastPageVisible, ...restProps }) => {
  const {
    id,
    pages,
    photo,
    mobilePhoto,
    video,
    info: { eventDisplayName, greeting },
  } = event;
  const { eventInfo } = useEventInfo();

  // Filter out legacy pages that have been migrated to custom pages
  const { filteredPages } = useFilterMigratedLegacyPages(pages);
  const { eventDesign } = event;
  const isMobileOrTablet = useIsMobileOrTablet();
  const { isCustomArtEnabled, customArtUrl } = useMemo(
    () => ({
      isCustomArtEnabled: getIsCustomArtEnabled(eventDesign),
      customArtUrl: isMobileOrTablet ? getCustomArtMobileUrl(eventDesign) : getCustomArtUrl(eventDesign, DesignLayoutType.aloha),
    }),
    [eventDesign, isMobileOrTablet],
  );

  const { isSmallScreen, loading, CustomStyle } = useAlohaDesign(eventHandle, event.eventDesign);
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const useMobilePhoto = isPortrait && isMobile;
  const homeRef = useRef<HTMLDivElement>(null);
  const isPreviewing = isInIframe();
  const containerRef = useRef(null);
  const pagesRef: Record<string, PagesRefs> = {};
  const { pageColor, pageFontColor, artBackgroundColor, artBackgroundFontColor } = useColorPaletteProvider();

  useAlohaScroll(filteredPages, pagesRef, containerRef.current, setIsLastPageVisible);
  const { findWelcomePage, isWelcomePageAsCustomPageEnabled } = useWelcomePage();

  const {
    handleMenuClicked,
    handleWelcomeRSVPButtonClicked,
    handleWelcomeViewDetailsButtonClicked,
    handleFooterButtonClicked,
    handleMenuOpenedChange,
    handleWelcomeOnNotYouButtonClicked,
    handleWelcomeOnNotYouErrorRequest,
    telemetryEditPhotoActions,
  } = useLayoutAlohaController({ eventHandle, pages: filteredPages });

  // Create eventProps with filtered pages for navigation menus
  const eventMenuProps = useMemo(() => ({ ...getEventMenuPropsFromEvent(event), pages: filteredPages }), [event, filteredPages]);

  const { tq } = useTranslation('joykit');

  // Get website banner offset
  const { bannerOffset } = useWebsiteBannerOffset();

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, event.eventDesign?.theme.websiteLayoutAlohaSettings?.alohaColorPreference?.supportsCustomization]);

  const onEditThemeColorClick = useCallback(() => {
    // send the message to the parent window to open the theme editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: {
        action: 'editThemeColor',
        inlineEditData: {
          imageURL: filteredPages.find(p => findWelcomePage(p.pageSlug, p.type).isWelcomePage)?.photo?.url || '',
          eventDesignId: event.eventDesign?.id || '',
        },
      },
    });
  }, [event.eventDesign?.id, filteredPages, findWelcomePage]);

  const indexOfValidPageWidgetAfterWelcomePage = useMemo(() => {
    const indexOfWelcome = filteredPages.findIndex(p => {
      const { isWelcomePage } = findWelcomePage(p.pageSlug, p.type);
      return isWelcomePage;
    });
    // if welcome page is not found, then we don't need to disable the top margin
    if (indexOfWelcome === -1) {
      return -1;
    }
    const indexOfFirstValidPageAfterWelcome = filteredPages.slice(indexOfWelcome + 1).findIndex(p => pageWidgets[p.type as PreparedPages]);

    // Return -1 if no valid page found, otherwise calculate the actual index
    return indexOfFirstValidPageAfterWelcome === -1 ? -1 : indexOfFirstValidPageAfterWelcome + indexOfWelcome + 1;
  }, [filteredPages, findWelcomePage]);

  return (
    <FrameWrapper>
      {CustomStyle && <CustomStyle />}
      <PreloadImages pages={filteredPages} />
      <AuxFrameProvider
        isSmallScreen={isSmallScreen}
        eventId={id}
        primaryPhoto={useMobilePhoto ? mobilePhoto : photo}
        pages={filteredPages}
        isPreviewing={isPreviewing}
      >
        <StyledFlexAloha
          ref={containerRef}
          loading={loading}
          isPreviewing={isPreviewing}
          className="aloha-scrollable"
          heightOffset={bannerOffset}
        >
          <div
            id="photo-welcome"
            ref={homeRef}
          />
          {!isMobile && (
            <WebsiteHeader
              eventMenuProps={eventMenuProps}
              artBackgroundFontColor={artBackgroundFontColor}
              telemetryEditPhotoActions={telemetryEditPhotoActions}
              handleMenuOpenedChange={handleMenuOpenedChange}
              handleMenuClicked={handleMenuClicked}
              eventDisplayName={eventDisplayName}
              greeting={greeting}
            />
          )}
          <MainFrame backgroundColor={pageColor}>
            {filteredPages.map((page, idx) => {
              const { isWelcomePage } = findWelcomePage(page.pageSlug, page.type);

              if (isWelcomePage) {
                const isCustomVariant = isWelcomePageAsCustomPageEnabled && page.type === EventPageType.custom;

                return (
                  <PageContainer
                    id={page.pageSlug}
                    backgroundColor={artBackgroundColor}
                    textColor={artBackgroundFontColor}
                    key={page.id}
                    ref={ref => (pagesRef[page.id] = { ref, page })}
                    disableBottomMargin
                  >
                    <div>
                      {isMobile && (
                        <WebsiteHeader
                          eventMenuProps={eventMenuProps}
                          artBackgroundFontColor={artBackgroundFontColor}
                          telemetryEditPhotoActions={telemetryEditPhotoActions}
                          handleMenuOpenedChange={handleMenuOpenedChange}
                          handleMenuClicked={handleMenuClicked}
                          eventDisplayName={eventDisplayName}
                          greeting={greeting}
                        />
                      )}
                      {isCustomVariant ? (
                        <Box
                          position="relative"
                          {...(!isCustomArtEnabled ? simple.prop : {})}
                        >
                          {isCustomArtEnabled && <CustomArt backgroundImageUrl={customArtUrl} />}
                          <Box>
                            <Body
                              key={page.id}
                              data-testid={`layout-body-${page.pageSlug}`}
                              page={page}
                              event={event}
                              eventHandle={eventHandle}
                              pageTitle={page.pageTitle}
                              isWelcomePage={true}
                              position="relative"
                            />
                          </Box>
                        </Box>
                      ) : (
                        <InlineEditor
                          elementLabel={tq`Theme`}
                          wrapperType="actionInside"
                          actionData={{
                            editThemeColor: onEditThemeColorClick,
                          }}
                          componentName="welcomePageTheme"
                          pageName={page.type}
                          pageSlug={page.pageSlug}
                          wrapperCSS={{ zIndex: 'docked' }}
                          // No longer preventing user from choosing an art background color
                          isEligibleForInlineEditing={true}
                        >
                          <WelcomeCard
                            homeRef={homeRef}
                            page={page}
                            eventHandle={eventHandle}
                            hideEventCountdown={eventInfo?.settings.hideEventCountdown ?? false}
                            finalizedDate={event.info.finalizedDate}
                            location={event.info.location}
                            pages={pages}
                            onRSVPButtonClicked={handleWelcomeRSVPButtonClicked}
                            onViewDetailsButtonClicked={handleWelcomeViewDetailsButtonClicked}
                            onNotYouButtonClicked={handleWelcomeOnNotYouButtonClicked}
                            onNotYouErrorRequest={handleWelcomeOnNotYouErrorRequest}
                            isCustomArtEnabled={isCustomArtEnabled}
                            customArtUrl={customArtUrl}
                            {...restProps}
                          />
                        </InlineEditor>
                      )}
                      {video ? (
                        <WelcomeVideo
                          key="welcome-video"
                          video={video}
                          thumbnailUrl={video.thumbnailUrl || page.photo?.url}
                        />
                      ) : null}
                    </div>
                  </PageContainer>
                );
              }

              if (page.type === 'custom' && page?.pageSlug === 'accommodations') {
                return (
                  <CustomPageContainer
                    id={page.pageSlug}
                    backgroundColor={pageColor}
                    textColor={pageFontColor}
                    key={page.id}
                    ref={ref => (pagesRef[page.id] = { ref, page })}
                    disableMobileTopMargin={idx === indexOfValidPageWidgetAfterWelcomePage}
                  >
                    <Body
                      key={page.id}
                      data-testid={`layout-body-${page.pageSlug}`}
                      page={page}
                      event={event}
                      eventHandle={eventHandle}
                      applicatorProps={simple.prop || {}}
                      pageTitle={page.pageTitle}
                    />
                  </CustomPageContainer>
                );
              }

              if (!pageWidgets[page.type as PreparedPages]) {
                return null;
              }

              return (
                <PageContainer
                  id={page.type}
                  backgroundColor={pageColor}
                  textColor={pageFontColor}
                  key={page.id}
                  ref={ref => (pagesRef[page.id] = { ref, page })}
                  disableMobileTopMargin={idx === indexOfValidPageWidgetAfterWelcomePage}
                >
                  <Body
                    key={page.id}
                    data-testid={`layout-body-${page.pageSlug}`}
                    page={page}
                    event={event}
                    eventHandle={eventHandle}
                    applicatorProps={simple.prop || {}}
                    pageTitle={page.pageTitle}
                  />
                </PageContainer>
              );
            })}
            <Flex __css={alohaFooterContainerStyles}>
              <Footer
                onButtonClicked={handleFooterButtonClicked}
                data-testid={'layout-footer'}
              />
            </Flex>
          </MainFrame>
        </StyledFlexAloha>
      </AuxFrameProvider>
    </FrameWrapper>
  );
};

LayoutAloha.displayName = 'LayoutAloha';

export { LayoutAloha };
